import _ from 'lodash';
import { all, select, takeLatest, call, put } from 'redux-saga/effects';
import { saveAs } from 'file-saver';
import {
  getOrganization,
  getUsersByOrganization,
  updateOrganization as updateOrganizationApi,
  fetchOrgActivitySpreadsheet as fetchOrgActivitySpreadsheetApi,
  fetchBillingPeriods as fetchBillingPeriodsApi,
} from 'spw-api';
import { APIResponse } from 'spw-redux/types';
import { DEFAULT_COLORS, XLSX_MIME_TYPE } from 'spw-constants';
import {
  actions,
  fetchOrgActivitySpreadsheetFailed,
  fetchOrgActivitySpreadsheetSuccess,
  fetchOrgSuccess,
  FETCH_ORG,
  FETCH_ORG_ACTIVITY_SPREADSHEET,
  getCurrentOrganizationUsersSucceeded,
  GET_CURRENT_ORGANIZATION_USERS,
  updateOrganizationFailed,
  updateOrganizationSuccess,
  UPDATE_ORGANIZATION,
  FETCH_BILLING_PERIODS,
} from './actions';
import { handleError } from '../UI/messages/actions';
import { projectColors } from '../projects/selectors';
import { currentUser } from '../user/selectors';
import { fetchProjectColorsSucceeded } from '../projects/actions';
import { selectCurrentOrganization } from './selectors';
import { Organization } from './types';
import { IUser } from '../user/types';
import { IProjectColors } from '../projects/types';

function* fetchOrganization() {
  try {
    const user: IUser = yield select(currentUser);
    if (user?.organizationId) {
      const { data } = yield call(getOrganization, user.organizationId);
      if (data.organization) {
        yield put(fetchOrgSuccess(data.organization));
        // handle case where project is already loaded and no projectColors exist
        if (
          data.organization.organizationColors &&
          !_.eq(data.organization.organizationColors, DEFAULT_COLORS)
        ) {
          const currentProjectColors: IProjectColors = yield select(
            projectColors
          );
          if (
            (currentProjectColors &&
              _.eq(currentProjectColors, DEFAULT_COLORS)) ||
            !currentProjectColors ||
            !currentProjectColors.individualWorkspaces
          ) {
            yield put(
              fetchProjectColorsSucceeded(data.organization.organizationColors)
            );
          }
        }
      }
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getCurrentOrganizationUsers() {
  try {
    const currentOrg: Organization = yield select(selectCurrentOrganization);
    if (currentOrg) {
      const { data } = yield call(getUsersByOrganization, currentOrg.id);
      const users = data?.users;
      yield put(getCurrentOrganizationUsersSucceeded(users));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateOrganization(
  action: ReturnType<typeof actions.updateOrganization>
) {
  try {
    const { data }: APIResponse<typeof updateOrganizationApi> = yield call(
      updateOrganizationApi,
      action.payload
    );

    yield put(updateOrganizationSuccess(data.organization));
    if (action.payload.onSuccess) yield call(action.payload.onSuccess);
  } catch (e) {
    yield put(updateOrganizationFailed());
    yield put(handleError(e));
  }
}

function* fetchOrgActivitySpreadsheet(
  action: ReturnType<typeof actions.fetchOrgActivitySpreadsheet>
) {
  try {
    const {
      data,
    }: APIResponse<typeof fetchOrgActivitySpreadsheetApi> = yield call(
      fetchOrgActivitySpreadsheetApi,
      action.payload.orgId,
      action.payload.billingDate
    );

    saveAs(
      `data:${XLSX_MIME_TYPE};base64,${data.spreadsheet.data}`,
      data.spreadsheet.name
    );

    yield put(fetchOrgActivitySpreadsheetSuccess());
  } catch (e) {
    yield put(fetchOrgActivitySpreadsheetFailed());
    yield put(handleError(e));
  }
}

function* fetchBillingPeriods(
  action: ReturnType<typeof actions.fetchBillingPeriods>
) {
  try {
    const { data }: APIResponse<typeof fetchBillingPeriodsApi> = yield call(
      fetchBillingPeriodsApi,
      action.payload.orgId
    );

    yield put(
      actions.fetchBillingPeriodsSuccess(
        _.orderBy(
          data.billingPeriods.map((bp) => ({ id: bp.startDate, ...bp })),
          [(bp) => bp.startDate.toLowerCase()],
          ['desc']
        )
      )
    );
  } catch (e) {
    yield put(actions.fetchBillingPeriodsFailure());
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(FETCH_ORG, fetchOrganization),
    takeLatest(GET_CURRENT_ORGANIZATION_USERS, getCurrentOrganizationUsers),
    takeLatest(UPDATE_ORGANIZATION, updateOrganization),
    takeLatest(FETCH_ORG_ACTIVITY_SPREADSHEET, fetchOrgActivitySpreadsheet),
    takeLatest(FETCH_BILLING_PERIODS, fetchBillingPeriods),
  ]);
}

export default saga;
